import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSite } from "../lib/SiteContext";
import api from "../lib/api";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import CsvImportModal from "../components/CsvImportModal";
import WaterSnapshotCard from "../components/dashboard/WaterSnapshotCard";
import { ConfidencePill } from "../components/Pills";
import { Upload, Droplets, Plus, CloudRain } from "lucide-react";
import { fmt, fmtInt, shortDate } from "../lib/format";

const TT = { background: "#18181B", border: "1px solid #27272A", borderRadius: 8, fontSize: 12 };

const PERIODS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "year", label: "Year" },
];

function PeriodTabs({ value, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="water-period-tabs">
      {PERIODS.map((p) => (
        <button
          key={p.id}
          onClick={() => onChange(p.id)}
          data-testid={`water-period-${p.id}`}
          className={`px-3 py-1.5 transition-colors ${
            value === p.id
              ? "bg-foreground text-background"
              : "bg-transparent text-muted-foreground hover:bg-secondary"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function ManualEntryModal({ open, onClose, onSuccess, siteId }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rain, setRain] = useState("");
  const [consumption, setConsumption] = useState("");
  const [tank, setTank] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (!open) return null;

  const hasInput = rain || consumption || tank;
  const canSubmit = !!hasInput && !busy && !!siteId;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    const body = { date };
    if (rain) body.rain_mm = Number(rain);
    if (consumption) body.consumption_liters = Number(consumption);
    if (tank) body.tank_level_liters = Number(tank);
    try {
      await api.post(`/sites/${siteId}/water/daily`, body);
      setRain(""); setConsumption(""); setTank("");
      onSuccess?.();
      onClose();
    } catch (e2) {
      setErr(e2.userMessage || "Failed to save entry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="water-manual-modal">
      <form onSubmit={submit} className="w-full max-w-md card-base p-5">
        <h3 className="font-heading text-lg font-semibold">Add manual water entry</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Any combination of rainfall, consumption, or tank level. Empty fields are skipped — never overwritten.
        </p>
        <div className="space-y-3 mt-4">
          <div>
            <span className="label-tiny block mb-1.5">Date</span>
            <input
              type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid="water-manual-date"
              className="w-full px-3 py-2 rounded-md bg-zinc-950 border border-border text-sm"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <span className="label-tiny block mb-1.5">Rain (mm)</span>
              <input
                type="number" step="0.1" value={rain} onChange={(e) => setRain(e.target.value)}
                data-testid="water-manual-rain"
                placeholder="e.g. 4.5"
                className="w-full px-3 py-2 rounded-md bg-zinc-950 border border-border text-sm"
              />
            </div>
            <div>
              <span className="label-tiny block mb-1.5">Use (L)</span>
              <input
                type="number" step="1" value={consumption} onChange={(e) => setConsumption(e.target.value)}
                data-testid="water-manual-consumption"
                placeholder="e.g. 240"
                className="w-full px-3 py-2 rounded-md bg-zinc-950 border border-border text-sm"
              />
            </div>
            <div>
              <span className="label-tiny block mb-1.5">Tank (L)</span>
              <input
                type="number" step="100" value={tank} onChange={(e) => setTank(e.target.value)}
                data-testid="water-manual-tank"
                placeholder="e.g. 35000"
                className="w-full px-3 py-2 rounded-md bg-zinc-950 border border-border text-sm"
              />
            </div>
          </div>
          {err && <div className="text-xs text-[#EF4444]" data-testid="water-manual-error">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md text-sm border border-border">Cancel</button>
          <button
            type="submit" disabled={!canSubmit}
            data-testid="water-manual-submit"
            className="px-3 py-1.5 rounded-md bg-foreground text-background text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save entry"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Water() {
  const { currentSite } = useSite();
  const [period, setPeriod] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [snap, setSnap] = useState(null);
  const [openImport, setOpenImport] = useState(null);
  const [showManual, setShowManual] = useState(false);

  const reloadSummary = useCallback(async () => {
    if (!currentSite) return;
    const { data } = await api.get(`/sites/${currentSite.id}/water/summary?period=${period}`);
    setSummary(data);
  }, [currentSite, period]);

  const reloadSnapshot = useCallback(async () => {
    if (!currentSite) return;
    const { data } = await api.get(`/sites/${currentSite.id}/water/snapshot`);
    setSnap(data);
  }, [currentSite]);

  useEffect(() => { reloadSummary(); }, [reloadSummary]);
  useEffect(() => { reloadSnapshot(); }, [reloadSnapshot]);

  const reload = async () => {
    await Promise.all([reloadSummary(), reloadSnapshot()]);
  };

  const rows = summary?.rows || [];
  const breakdown = summary?.consumption_breakdown || null;
  const chart = useMemo(() => rows.map((r) => ({
    date: r.date,
    label: shortDate(r.date),
    rain: r.rain_mm == null ? null : Number(r.rain_mm),
    inflow: r.effective_rain_liters == null ? null : Number(r.effective_rain_liters),
    // The bar always reflects the *effective* consumption — measured
    // when available, estimated fallback when not. Days marked
    // ``missing`` (no measured value AND no site fallback) render as
    // null so they appear as gaps rather than zero bars.
    consumption: r.consumption_effective_liters == null
      ? null
      : Number(r.consumption_effective_liters),
    consumption_status: r.consumption_status,
    tank: r.simulated_tank_level_liters,
    anchor: r.tank_level_liters,
  })), [rows]);

  return (
    <div className="space-y-8 pb-12" data-testid="water-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="label-tiny mb-1.5 flex items-center gap-2"><Droplets className="w-3.5 h-3.5 text-[#3B82F6]" /> Water</div>
          <h1 className="font-heading text-3xl font-semibold">Tank & rainwater</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Estimated tank volume, rainfall and consumption · rainwater-first
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowManual(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-xs hover:bg-secondary" data-testid="water-add-manual">
            <Plus className="w-3.5 h-3.5" /> Add manual entry
          </button>
          <button onClick={() => setOpenImport("pump")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-xs hover:bg-secondary" data-testid="water-import-pump">
            <Upload className="w-3.5 h-3.5" /> Pump kWh CSV
          </button>
          <button onClick={() => setOpenImport("water")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-xs hover:bg-secondary" data-testid="water-import-water">
            <Upload className="w-3.5 h-3.5" /> Manual water CSV
          </button>
        </div>
      </header>

      {/* Snapshot card */}
      <WaterSnapshotCard siteId={currentSite?.id} />

      {/* Period selector */}
      <section className="flex items-center justify-between gap-3 flex-wrap" data-testid="water-period-summary">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="label-tiny flex items-center gap-2">
            <CloudRain className="w-3.5 h-3.5 text-[#3B82F6]" /> Period
          </h2>
          {summary && (
            <div className="text-[11px] text-muted-foreground">
              {summary.start === summary.end
                ? shortDate(summary.start)
                : `${shortDate(summary.start)} → ${shortDate(summary.end)}`}
              {" · "}{summary.days_with_data} day{summary.days_with_data === 1 ? "" : "s"} of data
            </div>
          )}
        </div>
        <PeriodTabs value={period} onChange={setPeriod} />
      </section>

      <section className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="card-base p-4" data-testid="water-period-consumption">
          <div className="label-tiny">Consumption</div>
          <div className="font-heading text-2xl mt-1.5 tabular-nums">
            {fmtInt(summary?.consumption_liters || 0)}
            <span className="text-xs text-muted-foreground ml-1 font-normal">L</span>
          </div>
          {breakdown && breakdown.total_days > 0 && (
            <div
              className="mt-2 text-[11px] leading-snug text-muted-foreground"
              data-testid="water-consumption-breakdown"
            >
              {/* One-line measured / estimated / manual breakdown. We
                  intentionally render the numbers FIRST, then the day
                  counters in dim text, so the eye lands on the
                  human-meaningful figures.  */}
              {(breakdown.measured_liters > 0 || breakdown.manual_liters > 0) ? (
                <>
                  <span className="text-foreground">
                    {fmtInt(breakdown.measured_liters + breakdown.manual_liters)} L measured
                  </span>
                  {breakdown.estimated_liters > 0 && (
                    <>
                      <span className="mx-1">·</span>
                      <span>{fmtInt(breakdown.estimated_liters)} L estimated</span>
                    </>
                  )}
                </>
              ) : breakdown.estimated_liters > 0 ? (
                <span>{fmtInt(breakdown.estimated_liters)} L estimated</span>
              ) : (
                <span>no measured data</span>
              )}
              <div className="opacity-70 mt-0.5">{breakdown.coverage_label}</div>
              {breakdown.missing_days > 0 && breakdown.fallback_per_day_liters == null && (
                <div className="text-amber-400 mt-0.5">
                  Set a daily usage baseline in Settings to estimate missing days.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="card-base p-4" data-testid="water-period-rain">
          <div className="label-tiny">Rainfall</div>
          <div className="font-heading text-2xl mt-1.5 tabular-nums text-[#60A5FA]">
            {fmt(summary?.rain_mm || 0, 1)}
            <span className="text-xs text-muted-foreground ml-1 font-normal">mm</span>
          </div>
        </div>
        <div className="card-base p-4" data-testid="water-period-inflow">
          <div className="label-tiny">Effective inflow</div>
          <div className="font-heading text-2xl mt-1.5 tabular-nums text-[#3B82F6]">
            {fmtInt(summary?.effective_rain_liters || 0)}
            <span className="text-xs text-muted-foreground ml-1 font-normal">L</span>
          </div>
        </div>
      </section>

      {/* Tank volume trend */}
      <section className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-tiny">Tank volume trend</div>
            <div className="text-xs text-muted-foreground mt-1">
              Capacity {fmtInt(currentSite?.tank_capacity_liters)} L · anchored to manual readings
            </div>
          </div>
          <ConfidencePill level={snap?.tank_source === "manual" ? "manual" : "simulated"} />
        </div>
        <div className="h-64" data-testid="water-tank-chart">
          <ResponsiveContainer>
            <AreaChart data={chart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="tankFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <Tooltip contentStyle={TT} />
              <Area type="monotone" dataKey="tank" name="Simulated" stroke="#3B82F6" strokeWidth={2} fill="url(#tankFill)" />
              <Line type="monotone" dataKey="anchor" name="Manual" stroke="#FAFAFA" strokeWidth={0} dot={{ fill: "#FAFAFA", r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Consumption trend */}
      <section className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="label-tiny">Consumption trend</div>
          <div className="text-[11px] text-muted-foreground">liters per day</div>
        </div>
        <div className="h-56" data-testid="water-consumption-chart">
          <ResponsiveContainer>
            <BarChart data={chart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <Tooltip contentStyle={TT} />
              <Bar dataKey="consumption" name="Consumption" fill="#93C5FD" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Rainfall trend */}
      <section className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="label-tiny">Rainfall trend</div>
          <div className="flex gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#3B82F6]"/>mm</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#60A5FA]"/>captured L</span>
          </div>
        </div>
        <div className="h-56" data-testid="water-rainfall-chart">
          <ResponsiveContainer>
            <BarChart data={chart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <YAxis yAxisId="mm" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <YAxis yAxisId="L" orientation="right" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <Tooltip contentStyle={TT} />
              <Bar yAxisId="mm" dataKey="rain" name="Rain (mm)" fill="#3B82F6" radius={[3, 3, 0, 0]} />
              <Bar yAxisId="L" dataKey="inflow" name="Inflow (L)" fill="#60A5FA" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <ManualEntryModal
        open={showManual}
        onClose={() => setShowManual(false)}
        onSuccess={reload}
        siteId={currentSite?.id}
      />

      <CsvImportModal
        open={openImport === "pump"}
        onClose={() => setOpenImport(null)}
        onSuccess={reload}
        title="Pump socket cumulative kWh"
        description={`Converted to liters using your site's liters_per_kwh (${currentSite?.liters_per_kwh}).`}
        endpoint={`/sites/${currentSite?.id}/imports/pump`}
      />
      <CsvImportModal
        open={openImport === "water"}
        onClose={() => setOpenImport(null)}
        onSuccess={reload}
        title="Manual water CSV"
        description="Optional rain/consumption/tank columns. Existing values are preserved unless explicitly set."
        endpoint={`/sites/${currentSite?.id}/imports/water`}
      />
    </div>
  );
}
